// 控制「行銷管理頁面」區域
import api from "@/assets/js/utils/api.js"; // 呼叫後端 API
import {
  showSuccess,
  showConfirm,
  showToast,
  showError,
} from "@/assets/js/utils/alertService.js"; // sweetalert2 封裝

export default {
  name: "MarketingManagement", // 元件名稱

  data() {
    return {
      // 儲值金設定
      storedValueSettings: {
        enabled: true,
        bonusRate: 5,
      },

      // 點數設定
      pointsSettings: {
        enabled: false,
        amountPerPoint: 100,
        expiryDays: 365,
      },

      // 優惠券 UI 狀態
      searchQuery: "",
      showExpiredCoupons: false,
      showCouponModal: false,
      editingCoupon: null,

      // ⭐後端需求格式
      couponForm: {
        couponName: "",
        description: "",
        couponType: "",
        discountValue: 0,
        itemId: 0,
        startDate: "",
        endDate: "",
        usedCount: 0,
        maxUsageCount: 0,
        isActive: true,
      },

      coupons: [], // 優惠券資料
      productList: [], // 商品列表

      // ✅ 分頁相關
      currentPage: 1, // 目前頁數
      pageSize: 6, // 一頁 6 筆
    };
  },

  computed: {
    filteredCoupons() {
      let result = this.coupons;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter((coupon) =>
          coupon.couponName.toLowerCase().includes(query)
        );
      }

      if (!this.showExpiredCoupons) {
        result = result.filter((coupon) => !coupon.isExpired);
      }

      return result;
    },

    // ✅ 分頁後的資料
    pagedCoupons() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredCoupons.slice(start, start + this.pageSize);
    },

    // ✅ 總頁數
    totalPages() {
      return Math.ceil(this.filteredCoupons.length / this.pageSize);
    },
  },

  watch: {
    // ✅ 當顯示過期切換後，若頁數不合法，自動修正
    showExpiredCoupons() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages || 1;
      }
    },

    // ✅ 當搜尋關鍵字變更 → 回第一頁（建議保留）
    searchQuery() {
      this.currentPage = 1;
    },

    // ✅ 避免任何篩選導致頁碼落空
    filteredCoupons() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages || 1;
      }
    },
  },

  methods: {
    // ✅ 儲值功能提示
    saveStoredValue() {
      showToast(
        `儲值功能已${this.storedValueSettings.enabled ? "啟用" : "停用"}`,
        "success"
      );
    },

    savePoints() {
      showToast(
        `點數功能已${this.pointsSettings.enabled ? "啟用" : "停用"}`,
        "success"
      );
    },

    // ✅ 開新 modal
    openAddCouponModal() {
      this.editingCoupon = null;

      this.couponForm = {
        couponName: "",
        description: "",
        couponType: "",
        discountValue: 0,
        itemId: 0,
        startDate: "",
        endDate: "",
        maxUsageCount: 0,
        isActive: true,
      };

      this.showCouponModal = true;
    },

    // ✅ 編輯
    editCoupon(coupon) {
      this.editingCoupon = coupon;

      this.couponForm = {
        couponName: coupon.couponName,
        description: coupon.description,
        couponType: coupon.couponType,
        discountValue: coupon.discountValue,
        itemId: coupon.itemId,
        startDate: coupon.startDate,
        endDate: coupon.endDate,
        maxUsageCount: coupon.maxUsageCount,
        isActive: coupon.isActive,
      };

      this.showCouponModal = true;
    },

    // ✅ 後端取優惠券資料
    async fetchCoupons() {
      try {
        const res = await api.get("/coupon");
        const data = Array.isArray(res.data) ? res.data : res.data.data;

        this.coupons = data.map((c) => ({
          id: c.Id,
          couponName: c.CouponName,
          description: c.Description,
          couponType: c.CouponType,
          discountValue: c.DiscountValue,
          itemId: c.ItemId,
          productName: c.ProductName,
          startDate: c.StartDate ? c.StartDate.split("T")[0] : "",
          endDate: c.EndDate ? c.EndDate.split("T")[0] : "",
          maxUsageCount: c.MaxUsageCount,
          usedCount: c.UsedCount,
          isExpired: c.EndDate ? new Date(c.EndDate) < new Date() : false,
          isActive: c.IsActive,
        }));

        this.currentPage = 1; // ✅ 每次取完資料重置頁碼
      } catch (err) {
        console.error(err);
        return showError(err.response?.data?.message || "無法獲取優惠券資料");
      }
    },

    // ✅ 新增
    async createCoupon() {
      try {
        await api.post("/coupon", this.couponForm);

        showSuccess("新增成功！");
        this.fetchCoupons();
        this.closeModal();
      } catch (err) {
        showError(err.response?.data?.message || "新增失敗");
      }
    },

    // ✅ 更新
    async updateCoupon() {
      try {
        const id = this.editingCoupon.id;
        await api.put(`/coupon/${id}`, this.couponForm);

        showSuccess("更新成功！");
        this.fetchCoupons();
        this.closeModal();
      } catch (err) {
        showError(err.response?.data?.message || "更新失敗");
      }
    },

    // ✅ 刪除
    async deleteCoupon(id) {
      const coupon = this.coupons.find((c) => c.id === id);

      const confirmed = await showConfirm(
        `確定要刪除優惠券「${coupon.couponName}」嗎？`
      );
      if (!confirmed.isConfirmed) return;

      try {
        await api.delete(`/coupon/${id}`);

        showSuccess("刪除成功！");
        this.fetchCoupons();

        // ✅ 避免刪到最後一筆頁面空白 → 自動往前一頁
        if (this.currentPage > 1 && this.pagedCoupons.length === 0) {
          this.currentPage--;
        }
      } catch (err) {
        showError(err.response?.data?.message || "刪除失敗");
      }
    },

    // ✅ 儲存（新增 or 更新）
    async saveCoupon() {
      if (this.editingCoupon) {
        await this.updateCoupon();
      } else {
        await this.createCoupon();
      }
    },

    // ✅ 關閉 modal
    closeModal() {
      this.showCouponModal = false;
      this.editingCoupon = null;
    },

    // ✅ 顯示類型文字
    getCouponTypeLabel(type) {
      const map = {
        Fixed: "固定折抵",
        Percentage: "百分比折扣",
        Product: "兌換商品",
      };
      return map[type] || type;
    },

    getCouponTypeClass(type) {
      switch (type) {
        case "Product":
          return "product"; // 兌換商品
        case "Fixed":
          return "fixed"; // 固定折抵
        case "Percentage":
          return "percentage"; // 百分比折扣
        default:
          return "";
      }
    },
    // ✅ 折扣文字顯示
    getCouponAmountText(coupon) {
      if (coupon.couponType === "Fixed") return `NT$ ${coupon.discountValue}`;
      if (coupon.couponType === "Percentage") return `${coupon.discountValue}%`;
      if (coupon.couponType === "Product") return `兌換：${coupon.productName}`;
      return "";
    },

    // ✅ 商品列表
    async fetchProducts() {
      try {
        const res = await api.get("/product/list");
        const data = Array.isArray(res.data) ? res.data : res.data.data;

        this.productList = data.map((p) => ({
          id: p.Id,
          name: p.ProductName,
        }));
      } catch (err) {
        showError("無法取得商品列表");
      }
    },

    // ✅ 狀態文字
    getStatusText(coupon) {
      if (coupon.isExpired) return "已過期";
      if (!coupon.isActive) return "停用";
      return "啟用";
    },

    // ✅ 狀態顏色 class
    getStatusClass(coupon) {
      if (coupon.isExpired) return "expired";
      if (!coupon.isActive) return "disabled";
      return "active";
    },

    // ✅ 下一頁
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    // ✅ 上一頁
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    // ✅ 搜尋 reset 頁碼
    handleSearch() {
      this.currentPage = 1;
    },
  },

  mounted() {
    this.fetchCoupons();
    this.fetchProducts();
  },
};
